import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  Zap, LayoutDashboard, Package, Calculator, Radar, Wallet, Warehouse,
  Landmark, LifeBuoy, Settings, LogOut, Menu, X, Users, ShieldCheck, Truck, IndianRupee, RotateCcw, PlusCircle, Scale, FileUp, FileText, Crosshair, Network,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AdminNotifier } from "@/components/AdminNotifier";
import { NdrWatcher } from "@/components/NdrWatcher";

const MERCHANT_NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/add-order", label: "Add Order", icon: PlusCircle },
  { to: "/dashboard/orders", label: "Orders", icon: Package },
  { to: "/dashboard/rates", label: "Rate Calculator", icon: Calculator },
  { to: "/dashboard/tracking", label: "Tracking", icon: Radar },
  { to: "/dashboard/tracker", label: "Live Tracker", icon: Crosshair },
  { to: "/dashboard/ratecard", label: "Rate Card", icon: FileText },
  { to: "/dashboard/reseller", label: "Reseller", icon: Network },
  { to: "/dashboard/invoices", label: "Invoices", icon: FileUp },
  { to: "/dashboard/ndr", label: "NDR", icon: RotateCcw },
  { to: "/dashboard/weight", label: "Weight Review", icon: Scale },
  { to: "/dashboard/trucks", label: "Trucks", icon: Truck },
  { to: "/dashboard/wallet", label: "Wallet", icon: Wallet },
  { to: "/dashboard/warehouses", label: "Warehouses", icon: Warehouse },
  { to: "/dashboard/cod", label: "COD", icon: IndianRupee },
  { to: "/dashboard/kyc", label: "KYC", icon: ShieldCheck },
  { to: "/dashboard/tickets", label: "Support", icon: LifeBuoy },
  { to: "/dashboard/settings", label: "Settings", icon: Settings },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/merchants", label: "Merchants", icon: Users },
  { to: "/admin/kyc", label: "KYC Queue", icon: ShieldCheck },
  { to: "/admin/cod", label: "COD Remittance", icon: IndianRupee },
  { to: "/admin/orders", label: "All Orders", icon: Package },
  { to: "/admin/labels", label: "Labels", icon: FileUp },
  { to: "/admin/invoices", label: "Invoices", icon: FileText },
  { to: "/admin/trucks", label: "Truck Bookings", icon: Truck },
  { to: "/admin/tickets", label: "Tickets", icon: LifeBuoy },
  { to: "/admin/warehouses", label: "Warehouses", icon: Warehouse },
  { to: "/admin/weight", label: "Weight Review", icon: Scale },
  { to: "/admin/settings", label: "Settings", icon: Settings },
  { to: "/dashboard", label: "My Store", icon: Warehouse },
];

const SidebarContent = ({ role, onNavigate }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = role === "admin" ? ADMIN_NAV : MERCHANT_NAV;
  const doLogout = () => {
    logout();
    navigate("/login");
  };
  return (
    <div className="flex flex-col h-full">
      <Link to="/" data-testid="sidebar-logo" className="flex items-center gap-2 font-display font-black text-lg text-white px-5 h-16 border-b border-white/10">
        <img src="/logo.png" alt="ShipZilla logo" className="w-8 h-8 rounded-xl" />
        SHIPZILLA
        {role === "admin" && (
          <span className="ml-1 text-[9px] font-mono font-bold bg-blaze text-white px-1.5 py-0.5 uppercase tracking-widest">Admin</span>
        )}
      </Link>
      <nav className="flex-1 py-4 overflow-y-auto">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`sidebar-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-3 text-sm font-semibold transition-colors duration-150 border-l-4 ${
                isActive
                  ? "text-sky-300 border-acid bg-white/5"
                  : "text-zinc-400 border-transparent hover:text-white hover:bg-white/5"
              }`
            }
          >
            <n.icon className="w-4 h-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-5">
        <p className="text-white text-sm font-bold truncate">{user?.name}</p>
        <p className="text-zinc-500 text-xs font-mono truncate mt-0.5">{user?.email}</p>
        <p className="text-zinc-500 text-xs font-mono truncate mt-0.5">{user?.business}</p>
        <button
          onClick={doLogout}
          data-testid="logout-button"
          className="mt-4 w-full flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-white bg-acid px-4 py-2.5 hover:bg-white transition-colors duration-150"
        >
          <LogOut className="w-3.5 h-3.5" /> Logout
        </button>
      </div>
    </div>
  );
};

export default function DashboardLayout({ role }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div data-testid={`${role}-layout`} className="min-h-screen bg-paper text-obsidian flex">
      <aside className="hidden md:block w-64 shrink-0 bg-obsidian fixed inset-y-0 left-0 z-40">
        <SidebarContent role={role} />
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-obsidian/60" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 inset-y-0 w-64 bg-obsidian">
            <button
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
              data-testid="mobile-menu-close"
              className="absolute top-4 right-4 text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent role={role} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}
      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b-2 border-obsidian flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open"
              className="md:hidden p-2 border-2 border-obsidian"
            >
              <Menu className="w-4 h-4" />
            </button>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
              {role === "admin" ? "Admin Console" : "Control Tower"} <span className="text-blaze">/ Live</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest text-zinc-500">
              <span className="w-2 h-2 bg-acid border border-obsidian rounded-none animate-pulse" />
              Same data as the app
            </span>
            <div className="w-9 h-9 bg-obsidian text-sky-300 font-display font-black flex items-center justify-center text-sm">
              {(user?.name || "S").charAt(0).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
        {role === "admin" && <AdminNotifier />}
        {role === "merchant" && <NdrWatcher />}
      </div>
    </div>
  );
}
